import {
  useMemo,
  useState,
  useEffect,
  useCallback,
  type KeyboardEvent,
} from 'react'
import {
  toPrecision,
  type NumericSeparators,
  getLocaleNumericSeparators,
  parseNumberFromPartialString,
  isDigit,
} from '@/utils/number-util'

/**
 * Configuration for the Numeric Input hook.
 */
export interface NumericInputHookConfig
  extends NumericValueOptions,
    NumericPrecisionOptions,
    NumericLocaleOptions,
    NumericGroupingOptions {
  /**
   * Whether to emit changes in real-time as the inner string
   * value changes, or whether to let the consumer use `connector.commit()`
   * to do so.
   */
  mode?: 'live' | 'draft'
}

/**
 * Options for numeric value handling.
 */
interface NumericValueOptions {
  /**
   * The numeric value.
   */
  value?: number | null | undefined
  /**
   * Handler to call when the value changes.
   * Will be called in real-time if `mode` is `live` (default).
   */
  onChange?: (newValue: number | null) => unknown
}

/**
 * Options for numeric precision.
 */
interface NumericPrecisionOptions {
  /**
   * The precision to round to.
   */
  precision?: number
}

/**
 * Options for locale-specific formatting.
 */
interface NumericLocaleOptions {
  /**
   * Locale to use for number formatting.
   */
  locale?: string
}

/**
 * Options for number grouping.
 */
interface NumericGroupingOptions {
  /**
   * Whether to use grouping separators, such as thousands separators.
   */
  useGrouping?: Intl.NumberFormatOptions['useGrouping']
}

/**
 * Options for formatting value for input.
 */
interface FormatValueOptions
  extends NumericPrecisionOptions,
    NumericLocaleOptions,
    NumericGroupingOptions {}

/**
 * Options for parsing numbers.
 */
interface ParseNumberOptions extends NumericLocaleOptions {}

/**
 * Numeric input controller.
 */
export function useNumericInput({
  value,
  onChange,
  precision,
  locale,
  useGrouping,
  mode,
}: NumericInputHookConfig): NumericConnector {
  // The actual textual input value.
  const [innerValue, setInputValue] = useState(
    formatValueForInput(value, {
      precision,
      locale,
      useGrouping,
    }),
  )

  // When the value prop changes, if the change was triggered
  // by something other than an input change, set the inner value.
  useEffect(() => {
    setInputValue(
      formatValueForInput(value, {
        precision,
        locale,
        useGrouping,
      }),
    )
  }, [value, precision, locale, useGrouping])

  // Used for changing the string value.
  const changeValue = useCallback(
    (str: string, noFormat = false): string => {
      const formattedValue = formatValueForInput(str, {
        precision,
        locale,
        useGrouping,
      })
      setInputValue(noFormat ? String(str) : formattedValue)
      return String(str)
    },
    [precision, locale, useGrouping],
  )

  // Emits the numeric value to the consumer.
  const emitValue = useCallback(
    (newValue: number | null) => {
      if (onChange && newValue !== value) {
        if (newValue === null) {
          onChange(null)
        } else {
          onChange(toPrecision(newValue, precision))
        }
      }
    },
    [onChange, value, precision],
  )

  // Changes the value and emits it to the consumer.
  const changeValueAndEmit = useCallback(
    (str: string, noFormat = false): void => {
      const strValue = changeValue(str, noFormat)
      if (isEmptyValue(strValue)) {
        emitValue(null)
      } else {
        const converted = parseNumber(strValue, { locale })
        if (!Number.isNaN(converted)) {
          emitValue(converted)
        } else {
          emitValue(0)
        }
      }
    },
    [changeValue, emitValue, locale],
  )

  // Whether the specified string is considered a valid
  // number text.
  const isValidNumericString = useCallback(
    (str: string) => {
      if (precision === 0) {
        return Array.from(str).every((ch) => isDigit(ch))
      }
      const separators = getSeparators(getLocale({ locale }))
      return Array.from(str).every(
        (ch) =>
          isDigit(ch) ||
          ch === '-' ||
          ch === separators.thousand ||
          ch === separators.decimal,
      )
    },
    [precision, locale],
  )

  // Can be used by the consumer to prevent key presses
  // that contain invalid characters.
  const keyDownPreventNonNumeric = useCallback(
    (e: KeyboardEvent) => {
      // Allow some control keys to be used.
      switch (e.key) {
        case 'Enter':
        case 'Esc':
        case 'Backspace':
        case 'Tab':
        case 'Delete':
          return true
      }

      if (e.metaKey || e.ctrlKey) {
        return true
      }

      // Allow arrow keys
      if (e.key.startsWith('Arrow')) {
        return true
      }

      if (!isValidNumericString(e.key)) {
        e.preventDefault()
        e.stopPropagation()
        return false
      }

      return true
    },
    [isValidNumericString],
  )

  // When using draft mode, used for committing the value to the consumer.
  const commit = useCallback(() => {
    changeValueAndEmit(innerValue)
  }, [changeValueAndEmit, innerValue])

  // The consumer can call this with a string to
  // change the inner textual value. Will be returned as `value`
  // on the connector.
  const onInnerValueChange = useCallback(
    (inputValue: string) => {
      if (mode === 'draft') {
        setInputValue(inputValue)
      } else {
        changeValueAndEmit(inputValue, true)
      }
    },
    [mode, changeValueAndEmit],
  )

  // The connector.
  return useMemo<NumericConnector>(
    () => ({
      changeValue,
      changeValueAndEmit,
      commit,
      isValidNumericString,
      keyDownPreventNonNumeric,
      value: innerValue,
      onChange: onInnerValueChange,
    }),
    [
      changeValue,
      changeValueAndEmit,
      commit,
      isValidNumericString,
      keyDownPreventNonNumeric,
      innerValue,
      onInnerValueChange,
    ],
  )
}

/**
 * The actions that can be triggered to interact with the
 * numeric input state.
 */
export interface NumericConnector {
  /**
   * Connect this to an `onChange` handler for the textual input.
   * @param value
   */
  onChange(value: string): unknown
  /**
   * The string-formatted number to render in the input field.
   */
  value: string
  /**
   * If `mode` is `draft`, call this when you want to emit a change.
   * If `mode` is `live`, then you shouldn't use this.
   */
  commit(): unknown
  /**
   * Changes the inner value but does not emit it.
   *
   * @param str the value to set
   * @param noFormat if `true`, does not format the value
   * @returns the formatted, fixed value
   */
  changeValue(str: string, noFormat?: boolean): string
  /**
   * Triggers a change to the value which will be emitted.
   *
   * @param {string} str the value to set
   * @param {boolean} noFormat if true, does not format the value
   */
  changeValueAndEmit(str: string, noFormat?: boolean): void
  /**
   * onKeyDown handler to prevent non-numeric characters from being entered into an input.
   */
  keyDownPreventNonNumeric(e: KeyboardEvent): unknown
  /**
   * Determines if the value consists solely of valid characters used
   * for composing a numeric string.
   *
   * @param {string} value
   */
  isValidNumericString(value: string): boolean
}

/**
 * Formats the value accordingly.
 *
 * @param {string} value the `Number` to format
 * @param {FormatValueOptions} options formatting options
 */
function formatValueForInput(
  value: string | number | undefined | null,
  options: FormatValueOptions,
) {
  if (isEmptyValue(value)) {
    return ''
  }

  const parsed = toPrecision(
    parseNumber(value, options),
    getSanePrecision(options),
  )

  if (Number.isNaN(parsed)) {
    return ''
  }

  return parsed.toLocaleString(getLocale(options), {
    minimumFractionDigits: getSanePrecision(options),
    useGrouping: options.useGrouping,
  })
}

/**
 * Gets sanitized precision.
 */
function getSanePrecision(options: NumericPrecisionOptions) {
  const prec = toPrecision(Number(options.precision), 0)
  return Number.isNaN(prec) ? 0 : Math.max(0, prec)
}

/**
 * Gets the locale from options.
 *
 * @param {NumericLocaleOptions} options
 */
function getLocale(options: NumericLocaleOptions) {
  return options.locale || navigator.language
}

/**
 * Determines if the value is empty (0 numeric does not count as empty)
 *
 * @param {*} value
 */
function isEmptyValue(
  value: number | string | null | undefined,
): value is null | undefined | '' {
  return value === null || value === undefined || value === ''
}

/**
 * Cache used for separator lookup.
 */
const separatorsCache = new Map<string, NumericSeparators>()

/**
 * `getLocaleNumericSeparators` with caching.
 *
 * @param {string} locale
 */
function getSeparators(locale: string) {
  if (!separatorsCache.has(locale)) {
    separatorsCache.set(locale, getLocaleNumericSeparators(locale))
  }

  return separatorsCache.get(locale)!
}

/**
 * Parses the number according to the specified options.
 *
 * @param {string} value
 * @param {ParseNumberOptions} options
 */
function parseNumber(value: string | number, options: ParseNumberOptions) {
  if (typeof value === 'number') {
    return value
  }
  const locale = getLocale(options)
  return parseNumberFromPartialString(value, getSeparators(locale))
}
